import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MemberPostNotificationSettings,
  MutationUpdateMemberPostNotificationSettingsArgs,
  PaginatedPost,
  Post,
} from '@tribeplatform/gql-client/types'

import type { QueryFilters } from '../../index.js'
import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMemberPostNotificationSettingsKey } from '../../utils/keys/notification.key.js'
import { getPostKey } from '../../utils/keys/post.key.js'
import {
  infinitePostArrayUpdater,
  infinitePostUpdater,
} from '../cache/useCachedPost.js'
import { pinnedPostsFilter, postsQueryFilter } from '../posts/filters.js'

type Snapshot = {
  snapshot: MemberPostNotificationSettings
  postSnapshot: Post
  postsSnapshots: [QueryKey, InfiniteData<PaginatedPost>][]
  pinnedPostsSnapshot: [QueryKey, InfiniteData<Post>][]
}

const updater =
  (memberPostNotificationSettingsEnabled: boolean) => (oldPost: Post) => ({
    ...oldPost,
    authMemberProps: {
      ...oldPost.authMemberProps,
      memberPostNotificationSettingsEnabled,
    },
  })

export const useUpdateMemberPostNotificationSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    MemberPostNotificationSettings,
    ClientError,
    MutationUpdateMemberPostNotificationSettingsArgs,
    Snapshot
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    MemberPostNotificationSettings,
    ClientError,
    MutationUpdateMemberPostNotificationSettingsArgs,
    Snapshot
  >(
    (input: MutationUpdateMemberPostNotificationSettingsArgs) =>
      client.notifications.updateMemberPostNotificationSettings(input),
    {
      onMutate: async newMemberPostNotificationSettings => {
        const { postId, input } = newMemberPostNotificationSettings
        const memberPostNotificationSettingsKey =
          getMemberPostNotificationSettingsKey({
            variables: {
              postId: newMemberPostNotificationSettings?.postId,
            },
          })
        await queryClient.cancelQueries(memberPostNotificationSettingsKey)
        const snapshot =
          queryClient.getQueryData<MemberPostNotificationSettings>(
            memberPostNotificationSettingsKey,
          )

        queryClient.setQueriesData<MemberPostNotificationSettings>(
          memberPostNotificationSettingsKey,
          oldMemberPostNotificationSettings => ({
            ...oldMemberPostNotificationSettings,
            ...newMemberPostNotificationSettings,
          }),
        )

        const postKey: QueryFilters = {
          queryKey: getPostKey({ variables: { id: postId } }),
          exact: false,
          type: 'active',
        }

        const postSnapshot = queryClient.getQueryData<Post>(postKey as any)

        if (postSnapshot) {
          queryClient.setQueriesData<Post>(postKey, updater(input.enabled))
        }
        const pinnedPostsSnapshot =
          queryClient.getQueriesData<InfiniteData<Post>>(pinnedPostsFilter)
        if (pinnedPostsSnapshot) {
          queryClient.setQueriesData<Post[]>(pinnedPostsFilter, oldPosts =>
            oldPosts.map(
              infinitePostArrayUpdater(postId, updater(input.enabled), true),
            ),
          )
        }

        // take a snapshot from all matched posts queries
        const postsSnapshots =
          queryClient.getQueriesData<InfiniteData<PaginatedPost>>(
            postsQueryFilter,
          )

        // update posts and replies within the found post everywhere (posts and the feed queries)
        queryClient.setQueriesData<InfiniteData<PaginatedPost>>(
          postsQueryFilter,
          infinitePostUpdater(postId, updater(input.enabled), false),
        )

        return { snapshot, postsSnapshots, pinnedPostsSnapshot, postSnapshot }
      },
      onError: (err, newMemberPostNotificationSettings, context) => {
        const { postId } = newMemberPostNotificationSettings
        const memberPostNotificationSettingsKey =
          getMemberPostNotificationSettingsKey({
            variables: {
              postId,
            },
          })

        queryClient.invalidateQueries(memberPostNotificationSettingsKey)
        if (context.postSnapshot) {
          const postKey = getPostKey({ variables: { id: postId } })
          queryClient.setQueriesData(postKey, context.postSnapshot)
        }
        if (context.pinnedPostsSnapshot) {
          context.pinnedPostsSnapshot.forEach(([queryKey, snapshot]) => {
            queryClient.setQueriesData(queryKey, snapshot)
          })
        }
        if (context.postsSnapshots) {
          context.postsSnapshots.forEach(([queryKey, snapshot]) => {
            queryClient.setQueriesData(queryKey, snapshot)
          })
        }
      },
      ...useMutationOptions,
    },
  )
}
